export type Interval = {
  start: number;
  end: number;
};

export type IntervalSet = Interval[];

/**
 * @param intervals The existing ranges
 * @param newInterval The new range we want to add and/or merge to the existing ranges.
 * @returns instructions into how to merge the ranges using splice
 *
 */
export const getIntervalSetInsertInstructions = (
  intervals: IntervalSet,
  newInterval: Interval
): {
  index: number;
  replaceCount: number;
  replacementInterval: Interval;
  willChange: boolean;
} => {
  /**** Algorithm Overview ****

                        0              1           2         3
    start chunks: <--------------|------------|------------|----->
                          [5, 10)     [15, 20)     [25, 30)
    end chunks:   <--------|-----------|------------|----------->
                        0         1           2            3

    Notice that the chunks have the same index in the gaps, but not when they
    overlap the an existing interval. This is convienet because then we can use
    endChunkIndex - startChunkIndex to get the number of intervals we'll want to
    replace with the replacementInterval.

    Examples:
                    [1,3)   [1, 8)   [8, 17)    [12, 22)   [20, 25)
    indexOfStart:     0        0        0          1          1
    indexOfEnd:       0        1        2          2          2

  */

  if (!intervals.length) {
    return {
      index: 0,
      replaceCount: 0,
      replacementInterval: Object.assign({}, newInterval),
      willChange: true,
    };
  }

  let startChunkIndex = intervals.findIndex(
    interval => newInterval.start <= interval.end
  );
  if (startChunkIndex === -1) {
    startChunkIndex = intervals.length;
  }

  let endChunkIndex = intervals.findIndex(
    interval => newInterval.end < interval.start
  );
  if (endChunkIndex === -1) {
    endChunkIndex = intervals.length;
  }

  let start: number;
  if (startChunkIndex === intervals.length) {
    start = newInterval.start;
  } else {
    start = Math.min(newInterval.start, intervals[startChunkIndex].start);
  }

  let end: number;
  if (endChunkIndex === 0) {
    end = newInterval.end;
  } else {
    end = Math.max(newInterval.end, intervals[endChunkIndex - 1].end);
  }

  const newIntervalEntirelyFitsInsideOfExistingInterval =
    startChunkIndex < intervals.length &&
    intervals[startChunkIndex].start <= newInterval.start &&
    intervals[startChunkIndex].end >= newInterval.end;

  return {
    index: startChunkIndex,
    replaceCount: endChunkIndex - startChunkIndex,
    replacementInterval: { start, end },
    willChange: !newIntervalEntirelyFitsInsideOfExistingInterval,
  };
};

export const updateIntervalSet = (
  intervals: IntervalSet,
  newInterval: Interval
): { intervals: IntervalSet; wasChanged: boolean } => {
  const { index, replaceCount, replacementInterval, willChange } =
    getIntervalSetInsertInstructions(intervals, newInterval);

  if (!willChange) {
    return {
      intervals: [...intervals],
      wasChanged: false,
    };
  }
  const updatedIntervals = [...intervals];
  updatedIntervals.splice(index, replaceCount, replacementInterval);
  return {
    intervals: updatedIntervals,
    wasChanged: true,
  };
};
